import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshNavLink as NavLink } from './RefreshLink';
import { IcPlus, IcPencil, IcPhone, IcCheck, IcChevronDown } from '../theme/icons';
import authService from '../services/authService';

// Shared sub-navigation for the « Bons de commande » area, same pattern as
// VenteTabs. Order: Nouvelle commande → Brouillons → Envoyés → Reçus.
// Desktop: horizontal strip. Mobile: a single button opening a dropdown
// (no side scroll — même esprit que la page « Nouvelle vente » mobile).
// The list tabs stay facturation-gated; « Nouvelle commande » is open to all.
const TABS = [
  { to: '/commandes/a-commander', label: 'Nouvelle commande', icon: IcPlus },
  { to: '/commandes/brouillons', label: 'Brouillons', icon: IcPencil, facturation: true },
  { to: '/commandes/envoyes', label: 'Envoyés', icon: IcPhone, facturation: true },
  { to: '/commandes/recus', label: 'Reçus', icon: IcCheck, facturation: true },
];

// `mobileExtra` (optionnel) : un nœud rendu à droite du bouton mobile, sur la
// même ligne (paire 50/50 comme les boutons Produits/Services de la vente).
// `children` (optionnel) : rendu à droite du strip desktop, aligné comme le
// toggle Produits/Services dans VenteTabs.
const CommandeTabs = ({ mobileExtra, children }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const tabs = TABS.filter((t) => !t.facturation || authService.hasFacturationAccess());
  const active = tabs.find((t) => location.pathname === t.to) || tabs[0];

  return (
    <div className="mb-4">
      {/* Mobile : tous les onglets dans un seul bouton → menu déroulant, même
          taille et design que le chip déroulant de « Nouvelle vente » (owner
          Studio 2026-08-14). `mobileExtra` (Produits/Labo) à droite sur la même
          ligne, comme le toggle Produits/Services de la vente. */}
      <div className="md:hidden flex gap-2 items-center">
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-white border-gray-900 text-gray-900 shadow-sm transition-colors"
          >
            <active.icon size={13} /> {active.label}
            <IcChevronDown size={11} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <div className="absolute z-40 top-full mt-1 left-0 min-w-[14rem] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
              {tabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-4 py-3 text-sm font-semibold border-b border-gray-50 last:border-b-0 ${
                      isActive ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                    }`
                  }
                >
                  <t.icon size={13} /> {t.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
        {mobileExtra && <div className="ml-auto inline-flex items-center gap-2 shrink-0">{mobileExtra}</div>}
      </div>

      {/* Desktop : strip horizontal — mêmes chips que VenteTabs (owner Studio
          2026-08-13 : actif = blanc bordure noire, inactif = gris). */}
      <div className="hidden md:block -mx-1 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="inline-flex gap-2 px-1 min-w-full items-center">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end
              className={({ isActive }) =>
                `shrink-0 ${isActive ? 'tab-chip-on' : 'tab-chip'}`
              }
            >
              <t.icon size={13} /> {t.label}
            </NavLink>
          ))}
          {children && (
            <div className="ml-auto inline-flex items-center gap-2 shrink-0">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandeTabs;
